import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight, Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";

const MOODS = ["Cheerful", "Calm", "Energetic", "Cosy", "Adventurous", "Nostalgic"];
const WEATHER = ["Hot", "Warm", "Pleasant", "Cool", "Rainy", "Cold"];

export default function AISuggest() {
  const enabled = useFeatureFlag("ai_flavor_suggest");
  const [form, setForm] = useState({ mood: "Cheerful", weather: "Hot", preferences: "" });
  const [busy, setBusy] = useState(false);
  const [picks, setPicks] = useState([]);

  const submit = async (e) => {
    e?.preventDefault(); setBusy(true); setPicks([]);
    try {
      const { data } = await api.post("/ai/flavor-suggest", form);
      setPicks(data.picks || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!enabled) return <Navigate to="/" replace />;

  return (
    <div data-testid="ai-suggest" className="section-pad">
      <div className="container-pp">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center gap-2"><Sparkles size={12} /> AI Flavour Curator</div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="editorial-h1 text-5xl md:text-7xl mt-4 leading-[0.95]">
              Tell us how you <em className="font-display italic">feel</em>. We'll tell you what to <em className="font-display italic">eat</em>.
            </motion.h1>
          </div>
          <div className="lg:col-span-4 text-brand-charcoal/70 leading-relaxed">
            <p>Three flavour picks, custom-curated by an AI trained on the Pop Panda catalog. Grounded in what we actually stock.</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-14 card-elevated p-8 lg:p-10 max-w-3xl space-y-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-2">Your mood</div>
            <div className="flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button type="button" key={m} onClick={() => setForm({ ...form, mood: m })}
                  className={`btn-pill text-xs ${form.mood === m ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{m}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-2">The weather right now</div>
            <div className="flex flex-wrap gap-2">
              {WEATHER.map((m) => (
                <button type="button" key={m} onClick={() => setForm({ ...form, weather: m })}
                  className={`btn-pill text-xs ${form.weather === m ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{m}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Anything else? (e.g. no chocolate, love mango, low-sugar)</label>
            <input className="pp-input" value={form.preferences} onChange={(e) => setForm({ ...form, preferences: e.target.value })} />
          </div>
          <button disabled={busy} className="btn-pill btn-dark inline-flex items-center gap-2" data-testid="ai-submit">
            {busy ? (<><Loader2 size={14} className="animate-spin" /> Thinking…</>) : (<>Get my picks <ArrowUpRight size={14} /></>)}
          </button>
        </form>

        {picks.length > 0 && (
          <div className="mt-14 grid sm:grid-cols-3 gap-5" data-testid="ai-picks">
            {picks.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12 }} className="card-elevated p-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-red">Pick {i + 1} {p.is_from_catalog === false && "· suggested"}</div>
                <div className="font-display text-2xl mt-2">{p.name}</div>
                {p.why && <p className="text-sm text-brand-charcoal/70 mt-3 leading-relaxed">{p.why}</p>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
